import { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { Edit, Eye, MoreVertical, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useDeleteNewsMediaMutation, useGetNewsMediaList } from "@/services";
import { toast } from "sonner";
import moment from "moment";

const useNewsMedia = (selectedTab?: string) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow, setDeletedRow] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const onAdd = (tab: string) => {
    navigate(`add`, { state: { applicableCompany: tab } });
  };

  const payload: any = {
    data: {
      limit: pageSize,
      orderBy: "updatedAt",
      orderDirection: "DESC",
      page: pageNumber + 1,
    },
  };

  // When MBAPL tab is selected, include the requested search object
  if (selectedTab === "MBAPL") {
    payload.data.search = {
      applicableCompany: "MBAPL",
    };
  } else if (selectedTab === "KPL") {
    payload.data.search = {
      applicableCompany: "KPL",
    };
  } else  {
    payload.data.search = {
      applicableCompany: "OSTWAL",
    };
  }

  const {
    data: newsMediaList,
    isLoading: isNewsMediaLoading,
    refetch: refetchNewsMedia,
    isRefetching: isNewsMediaRefetching,
  } = useGetNewsMediaList(payload);

  // Refetch when pagination or selected tab changes
  useEffect(() => {
    refetchNewsMedia();
  }, [pageNumber, pageSize, selectedTab]);

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteNewsMediaMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("News & Media Deleted Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({ queryKey: ["news-media"] });
        setOpenDialog(false);
      },
    });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "title",
      header: "Title",
      cell: (row: any) => (
        <b className="text-primary font-[600]">
          {String(row.row?.original?.title || "")}
        </b>
      ),
    },
    {
      accessorKey: "date",
      header: "Date",
      accessorFn: (row: any) =>
        row?.date ? moment(row?.date).format("MM-DD-YYYY") : "",
    },
    {
      accessorKey: "year",
      header: "Year",
      accessorFn: (row: any) => String(row.year || ""),
    },
    {
      accessorKey: "type",
      header: "Type",
      accessorFn: (row: any) => String(row.type || ""),
    },
    {
      accessorKey: "company",
      header: "Company",
      accessorFn: (row: any) => String(row.company || ""),
    },

    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`${row.row.original._id}/view`);
              }}
            >
              <Eye className="w-4 h-4" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`${row.row.original._id}/edit`);
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setDeletedRow(row.row.original);
                setOpenDialog(true);
              }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const onDeleteConfirm = () => {
    deleteInfo({ id: deletedRow?._id });
  };

  return {
    onAdd,
    columns,
    openDialog,
    deletedRow,
    setOpenDialog,
    isDeletePending,
    onDeleteConfirm,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    newsMediaList,
    isNewsMediaLoading,
    isNewsMediaRefetching,
  };
};

export default useNewsMedia;
